import React from 'react';
import styled from 'styled-components';

import { ErrorExclamationV2 as ErrorIcon } from 'src/svg/error-exclamation-v2';
import { GqlAllotmentInfo, AllotmentStatus } from 'types/graphql';
import { formatRemaining } from 'checkout/components/sections/mmur-allotment';

type MMURAlertBannerProps = {
  allotmentInfo: GqlAllotmentInfo | null;
  hasAllotmentError: boolean;
  shouldBlockOrder: boolean;
};

export default function MMURAlertBanner({
  allotmentInfo,
  hasAllotmentError,
  shouldBlockOrder,
}: MMURAlertBannerProps): JSX.Element | null {
  const isExceeded = allotmentInfo?.status === AllotmentStatus.exceeded;

  if (hasAllotmentError) {
    return (
      <Banner role='alert'>
        <Header>
          <ErrorIcon />
          <Title>MMUR Error</Title>
        </Header>
        <Description>
          {shouldBlockOrder
            ? 'MMUR allotment could not be verified. Please try again or contact the dispensary for assistance.'
            : 'MMUR allotment could not be verified. Purchasable items may be subject to change upon checkout at store.'}
        </Description>
      </Banner>
    );
  }

  if (!isExceeded) {
    return null;
  }

  const exceededCategories =
    allotmentInfo.details?.filter((d) => d?.status === AllotmentStatus.exceeded && !d.isUnlimited) ?? [];

  return (
    <Banner role='alert'>
      <Header>
        <ErrorIcon />
        <Title>MMUR Limit Exceeded</Title>
      </Header>
      <Description>
        {shouldBlockOrder
          ? 'Product(s) in your cart exceed your MMUR limit. Please remove applicable items to proceed with your order.'
          : 'Product(s) in your cart exceed your MMUR limit. Some products may not be approved for purchase at in-store checkout.'}
      </Description>
      {exceededCategories.length > 0 && (
        <>
          <BannerDivider />
          <ExceededDetails>
            {exceededCategories.map(
              (detail) =>
                detail && (
                  <span key={detail.category}>
                    {detail.category}:{' '}
                    <strong>{formatRemaining(detail.remainingWithCart ?? 0, detail.unit ?? 'mg')} remaining</strong>
                  </span>
                )
            )}
          </ExceededDetails>
        </>
      )}
    </Banner>
  );
}

const Banner = styled.div`
  background-color: #fae8ea;
  border-radius: 5px;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 28px 0 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.065px;
  color: #be0517;
`;

const Description = styled.p`
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.065px;
  color: #be0517;
  margin: 0;
`;

const BannerDivider = styled.hr`
  border: none;
  border-top: 1px solid #f2b3b9;
  margin: 0;
  width: 100%;
`;

const ExceededDetails = styled.div`
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.065px;
  color: #be0517;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
